import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';

import { ApiResponseError } from './ApiResponseError';

import type { ErrorEvent, EventHint } from '@sentry/react';

/**
 * Only the original startup error handled by the maintenance boundary is quiet.
 */
export const createStartupMaintenanceFilter = (
  client: QueryClient,
  queryKey: QueryKey,
) => {
  const handledErrors = new WeakSet<ApiResponseError>();
  const queryHash = hashKey(queryKey);
  let startupSucceeded = (client.getQueryState(queryKey)?.dataUpdatedAt ?? 0) > 0;

  // Keep successful startup sticky even across logout/cache removal. This also
  // observes seeded/anonymous users without changing the auth query's behavior.
  client.getQueryCache().subscribe(event => {
    if (
      event.type === 'updated' &&
      event.action.type === 'success' &&
      event.query.queryHash === queryHash
    ) {
      startupSucceeded = true;
    }
  });

  return {
    markHandled(error: unknown) {
      const state = client.getQueryState(queryKey);
      if (
        !startupSucceeded &&
        error instanceof ApiResponseError &&
        error.type === 'ERR_NETWORK' &&
        error.noResponse === true &&
        state?.status === 'error' &&
        state.dataUpdatedAt === 0 &&
        state.error === error
      ) {
        handledErrors.add(error);
      }
    },
    async beforeSend(event: ErrorEvent, hint: EventHint) {
      // The SDK can process global captures synchronously before componentDidCatch.
      // One microtask lets the boundary classify the same original error; no timer.
      await Promise.resolve();
      const error = hint.originalException;
      if (error instanceof ApiResponseError && handledErrors.has(error)) {
        // eslint-disable-next-line unicorn/no-null -- Sentry's required drop-event result
        return null;
      }
      return event;
    },
  };
};
